import React from 'react';

interface UseThresholdClickProps {
  onClick: () => void;
}

// WARNING: This hook relies on mutable variables that are not preserved across renders.
// It is designed to be used in a stable environment where the component does not re-render
// during the mouse interaction (e.g., a table row that does not update while being clicked).
// If used in a component that re-renders frequently, the behavior may be inconsistent.
export default function useThresholdClick<T>(props: UseThresholdClickProps) {
  let mouseIsDown = false;
  let mouseMoved = false;
  let mouseDownX = 0;
  let mouseDownY = 0;

  const ROW_CLICK_MOVE_THRESHOLD_PX = 5;

  function shouldIgnoreRowClick(target: EventTarget | null) {
    return target instanceof Element
      ? Boolean(
          target.closest(
            'button, a, input, select, textarea, label, [role="button"], [data-row-click-ignore]'
          )
        )
      : false;
  }

  function onMouseDown(e: React.MouseEvent<T, MouseEvent>) {
    if (shouldIgnoreRowClick(e.target)) return;

    mouseIsDown = true;
    mouseMoved = false;
    mouseDownX = e.clientX;
    mouseDownY = e.clientY;
  }

  function onMouseMove(e: React.MouseEvent<T, MouseEvent>) {
    if (!mouseIsDown) return;

    if (
      Math.abs(e.clientX - mouseDownX) > ROW_CLICK_MOVE_THRESHOLD_PX ||
      Math.abs(e.clientY - mouseDownY) > ROW_CLICK_MOVE_THRESHOLD_PX
    ) {
      mouseMoved = true;
    }
  }

  function onMouseUp(e: React.MouseEvent<T, MouseEvent>) {
    if (shouldIgnoreRowClick(e.target)) return;

    if (props.onClick && mouseIsDown && !mouseMoved) {
      props.onClick();
    }

    mouseIsDown = false;
    mouseMoved = false;
  }

  function onMouseLeave() {
    mouseIsDown = false;
  }

  return { onMouseDown, onMouseMove, onMouseUp, onMouseLeave };
}
